/**
 * client/src/lib/pqc.ts — Client-side Post-Quantum Cryptography
 *
 * Provides ML-DSA-65 (FIPS-204) digital signatures and ML-KEM-768 (FIPS-203)
 * key encapsulation for the client.
 *
 * Usage:
 *  - ML-DSA-65:  Sign identity events client-side for dual-signing (Rule 2)
 *  - ML-KEM-768: Encrypt vault seeds for guardian recovery
 *
 * Key generation is ephemeral by default — keys are generated fresh each
 * registration and not stored in localStorage.  The public keys are
 * registered with the user's profile (server-side).
 *
 * atomicWipe() is called on all secret key material after use (Rule 3).
 *
 * (NIST FIPS-204 ML-DSA / NIST FIPS-203 ML-KEM)
 */

import { ml_dsa65 }  from "@noble/post-quantum/ml-dsa";
import { ml_kem768 } from "@noble/post-quantum/ml-kem";
import { atomicWipe } from "./prf-key-derivation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MlDsaKeyPair {
  /** ML-DSA-65 public key (base64) — registered on the server profile */
  publicKey:  string;
  /** ML-DSA-65 secret key (Uint8Array) — kept in-memory, wiped after use */
  secretKey:  Uint8Array;
}

export interface MlKemKeyPair {
  /** ML-KEM-768 public key (base64) — registered on the server profile */
  publicKey:  string;
  /** ML-KEM-768 secret key (Uint8Array) — kept in-memory, wiped after use */
  secretKey:  Uint8Array;
}

export interface MlKemEncapsulated {
  /** Ciphertext to send to the recipient (base64) */
  ciphertext:   string;
  /** Shared secret — use as AES-256 key (Uint8Array). Wipe after use. */
  sharedSecret: Uint8Array;
}

// ---------------------------------------------------------------------------
// ML-DSA-65 — digital signatures (Rule 2)
// ---------------------------------------------------------------------------

/**
 * Generate a new ML-DSA-65 key pair.
 * The secret key is returned as a raw Uint8Array for in-memory use only.
 * Call atomicWipe(secretKey) immediately after signing.
 */
export function generateMlDsaKeyPair(): MlDsaKeyPair {
  const seed      = crypto.getRandomValues(new Uint8Array(32));
  const keyPair   = ml_dsa65.keygen(seed);
  atomicWipe(seed);

  return {
    publicKey: bytesToBase64(keyPair.publicKey),
    secretKey: keyPair.secretKey,
  };
}

/**
 * Sign a message with ML-DSA-65.
 *
 * @param secretKey  Raw secret key bytes. WIPED AFTER SIGNING.
 * @param message    The message to sign (UTF-8 string or Uint8Array).
 * @returns          Base64-encoded ML-DSA-65 signature.
 */
export function mlDsaSign(
  secretKey: Uint8Array,
  message:   string | Uint8Array
): string {
  const msgBytes = typeof message === "string"
    ? new TextEncoder().encode(message)
    : message;

  let signature: Uint8Array;
  try {
    signature = ml_dsa65.sign(secretKey, msgBytes);
  } finally {
    atomicWipe(secretKey);
  }

  return bytesToBase64(signature);
}

/**
 * Verify an ML-DSA-65 signature.
 *
 * @param publicKeyB64  Base64-encoded ML-DSA-65 public key.
 * @param message       The original message (UTF-8 string or Uint8Array).
 * @param signatureB64  Base64-encoded ML-DSA-65 signature.
 * @returns             true if the signature is valid.
 */
export function mlDsaVerify(
  publicKeyB64: string,
  message:      string | Uint8Array,
  signatureB64: string
): boolean {
  const publicKey = base64ToBytes(publicKeyB64);
  const msgBytes  = typeof message === "string"
    ? new TextEncoder().encode(message)
    : message;
  const signature = base64ToBytes(signatureB64);

  try {
    return ml_dsa65.verify(publicKey, msgBytes, signature);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// ML-KEM-768 — key encapsulation (guardian vault encryption)
// ---------------------------------------------------------------------------

/**
 * Generate a new ML-KEM-768 key pair.
 * Used for encrypting vault seeds for guardian recovery.
 */
export function generateMlKemKeyPair(): MlKemKeyPair {
  const seed    = crypto.getRandomValues(new Uint8Array(64));
  const keyPair = ml_kem768.keygen(seed);
  atomicWipe(seed);

  return {
    publicKey: bytesToBase64(keyPair.publicKey),
    secretKey: keyPair.secretKey,
  };
}

/**
 * Encapsulate a shared secret with ML-KEM-768.
 * Used by the sender to establish a shared secret with the recipient.
 *
 * @param recipientPublicKeyB64  The recipient's ML-KEM-768 public key (base64).
 * @returns  { ciphertext, sharedSecret } — send ciphertext to recipient;
 *           use sharedSecret as AES-256 key, then wipe it.
 */
export function mlKemEncapsulate(recipientPublicKeyB64: string): MlKemEncapsulated {
  const publicKey    = base64ToBytes(recipientPublicKeyB64);
  const seed         = crypto.getRandomValues(new Uint8Array(32));
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(publicKey, seed);
  atomicWipe(seed);

  return {
    ciphertext:   bytesToBase64(cipherText),
    sharedSecret: sharedSecret,
  };
}

/**
 * Decapsulate a shared secret with ML-KEM-768.
 *
 * @param secretKeyBytes  Raw ML-KEM-768 secret key. WIPED AFTER USE.
 * @param ciphertextB64   The ciphertext received from the sender (base64).
 * @returns  The 32-byte shared secret. Wipe with atomicWipe() after use.
 */
export function mlKemDecapsulate(
  secretKeyBytes: Uint8Array,
  ciphertextB64:  string
): Uint8Array {
  const ciphertext = base64ToBytes(ciphertextB64);
  let sharedSecret: Uint8Array;
  try {
    sharedSecret = ml_kem768.decapsulate(ciphertext, secretKeyBytes);
  } finally {
    atomicWipe(secretKeyBytes);
  }
  return sharedSecret;
}

// ---------------------------------------------------------------------------
// Bound payload signing (client-side dual-sign — Rule 2 / L0-7)
// ---------------------------------------------------------------------------

export interface ClientBoundPayload {
  sessionId:  string;
  eventType:  string;
  data:       Record<string, unknown>;
  timestamp:  string;
  nonce:      string;
}

/**
 * Build a bound payload for client-side signing.
 * Every signed payload must include sessionId and nonce (L0-7).
 */
export function buildClientBoundPayload(
  sessionId: string,
  eventType: string,
  data:      Record<string, unknown>
): ClientBoundPayload {
  return {
    sessionId,
    eventType,
    data,
    timestamp: new Date().toISOString(),
    nonce:     bytesToBase64(crypto.getRandomValues(new Uint8Array(16))),
  };
}

/**
 * Canonicalise a payload to a deterministic JSON string for signing.
 * Keys are sorted to ensure consistent output across environments.
 */
export function canonicalise(payload: ClientBoundPayload): string {
  return JSON.stringify(payload, Object.keys(payload).sort());
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary);
}

export function base64ToBytes(b64: string): Uint8Array {
  // Handle base64url format as well
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}
